import { apiClient } from '@/utils/api-client';
import type {
  FxCurrency,
  FxLatestRate,
  FxRate,
  FxRateBulkCreateRequest,
  FxRateCreateRequest,
  FxRateCreateResponse,
  FxRatePage,
  FxRateSyncRequest,
  FxRateSyncResponse,
  FxRateUpdateRequest,
} from '@/types/fx-rates';

export type FxRateListParams = {
  base_currency_id?: string;
  quote_currency_id?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
};

export const fxRatesService = {
  async listCurrencies(): Promise<FxCurrency[]> {
    return await apiClient.get('/admin/fx-rates/currencies');
  },

  async list(params?: FxRateListParams): Promise<FxRatePage> {
    return await apiClient.get('/admin/fx-rates', { params });
  },

  async listAll(
    params?: Omit<FxRateListParams, 'page' | 'page_size'> & {
      page_size?: number;
      max_pages?: number;
    }
  ): Promise<FxRate[]> {
    const { page_size, max_pages, ...listParams } = params ?? {};
    const pageSize = page_size ?? 365;
    const maxPages = max_pages ?? 100;

    let page = 1;
    const items: FxRate[] = [];

    while (page <= maxPages) {
      const result = await fxRatesService.list({
        ...listParams,
        page,
        page_size: pageSize,
      });

      if (result.items.length === 0) break;
      items.push(...result.items);

      if (items.length >= result.total_count) break;
      page += 1;
    }

    return items;
  },

  async create(payload: FxRateCreateRequest): Promise<FxRateCreateResponse> {
    return await apiClient.post('/admin/fx-rates', payload);
  },

  async createBulk(
    payload: FxRateBulkCreateRequest
  ): Promise<FxRateCreateResponse[]> {
    return await apiClient.post('/admin/fx-rates/bulk', payload);
  },

  async update(rateId: string, payload: FxRateUpdateRequest): Promise<FxRate> {
    return await apiClient.put(`/admin/fx-rates/${rateId}`, payload);
  },

  async delete(rateId: string): Promise<void> {
    await apiClient.delete(`/admin/fx-rates/${rateId}`);
  },

  async syncFromEcb(
    payload: FxRateSyncRequest = {}
  ): Promise<FxRateSyncResponse> {
    return await apiClient.post('/admin/fx-rates/sync', payload);
  },

  async latest(params?: {
    base_currency_id?: string;
    quote_currency_id?: string;
  }): Promise<FxLatestRate[]> {
    return await apiClient.get('/admin/fx-rates/latest', { params });
  },
};
